import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';

const Header = ({ showAdminDashboard, setShowAdminDashboard, setShowLoginPrompt }) => {
  const navigate = useNavigate();
  const { user, isAuthenticated, isAdmin } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  return (
    <nav className="fixed top-0 w-full z-50 bg-white/90 backdrop-blur-lg border-b border-orange-100">
      <div className="max-w-7xl mx-auto px-8">
        <div className="flex justify-between items-center h-16">
          <button 
            onClick={() => navigate('/')}
            className="flex items-center space-x-2 hover:scale-105 transition-transform duration-200"
          >
            <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">C</span>
            </div>
            <span className="text-xl font-bold text-orange-600">CBT 편의점</span>
          </button>
          
          {/* Desktop Menu */}
          <div className="hidden md:flex space-x-8 text-sm font-medium">
            <a href="/#features" className="text-gray-600 hover:text-orange-600 transition-colors">기능소개</a>
            <a href="/#exams" className="text-gray-600 hover:text-orange-600 transition-colors">시험종류</a>
            {/* 모의고사 기능 임시 비활성화 - AI 품질 개선 후 재활성화 예정
            <button 
              onClick={() => navigate('/mock-exams')}
              className="text-gray-600 hover:text-orange-600 transition-colors font-medium"
            >
              모의고사
            </button>
            */}
            <a href="/#testimonials" className="text-gray-600 hover:text-orange-600 transition-colors">이용후기</a>
          </div>
          
          <div className="flex items-center space-x-4">
            {/* Desktop Login/User */}
            <div className="hidden md:flex items-center space-x-4">
              {isAuthenticated ? (
                <button 
                  onClick={() => navigate('/dashboard')}
                  className="w-8 h-8 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center hover:from-orange-600 hover:to-orange-700 transition-all duration-200 hover:scale-105"
                  title={`개인 대시보드 - ${user?.name}`}
                >
                  <span className="text-white font-bold text-sm">
                    {user?.email ? user.email.charAt(0).toUpperCase() : user?.name?.charAt(0).toUpperCase() || 'U'}
                  </span>
                </button>
              ) : (
                <button 
                  onClick={() => setShowLoginPrompt && setShowLoginPrompt(true)}
                  className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-4 py-2 rounded-lg hover:from-orange-600 hover:to-orange-700 transition-all font-medium text-sm"
                >
                  로그인
                </button>
              )}

              {/* 관리자 대시보드 - 관리자로 로그인했을 때만 표시 */}
              {isAdmin && (
                <button 
                  onClick={() => setShowAdminDashboard && setShowAdminDashboard(true)}
                  className="w-8 h-8 bg-red-500 text-white rounded-lg text-sm font-bold hover:bg-red-600 transition-colors flex items-center justify-center"
                  title="관리자 대시보드"
                >
                  A
                </button>
              )}
            </div>
            
            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              {isMobileMenuOpen ? (
                <XMarkIcon className="w-6 h-6 text-gray-600" />
              ) : (
                <Bars3Icon className="w-6 h-6 text-gray-600" />
              )}
            </button>
          </div>
        </div>
        
        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden absolute top-16 right-0 bg-white/95 backdrop-blur-lg border border-orange-100 shadow-lg rounded-bl-lg">
            <div className="px-6 py-4 space-y-4 min-w-max">
              <a 
                href="/#features" 
                className="block text-gray-600 hover:text-orange-600 transition-colors font-medium text-center"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                기능소개
              </a>
              <a 
                href="/#exams" 
                className="block text-gray-600 hover:text-orange-600 transition-colors font-medium text-center"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                시험종류
              </a>
              {/* 모의고사 기능 임시 비활성화 - AI 품질 개선 후 재활성화 예정
              <button 
                onClick={() => {
                  navigate('/mock-exams');
                  setIsMobileMenuOpen(false);
                }}
                className="block text-gray-600 hover:text-orange-600 transition-colors font-medium text-center w-full"
              >
                모의고사
              </button>
              */}
              <a 
                href="/#testimonials" 
                className="block text-gray-600 hover:text-orange-600 transition-colors font-medium text-center"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                이용후기
              </a>
              
              {/* Mobile Login/User */}
              <div className="pt-4 border-t border-gray-200">
                {isAuthenticated ? (
                  <button 
                    onClick={() => {
                      navigate('/dashboard');
                      setIsMobileMenuOpen(false);
                    }}
                    className="flex items-center justify-center space-x-2 text-gray-600 hover:text-orange-600 transition-colors font-medium w-full"
                  >
                    <div className="w-4 h-4 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center">
                      <span className="text-white font-bold text-xs">
                        {user?.email ? user.email.charAt(0).toUpperCase() : user?.name?.charAt(0).toUpperCase() || 'U'}
                      </span>
                    </div>
                    <span>개인 대시보드</span>
                  </button>
                ) : (
                  <button 
                    onClick={() => {
                      setShowLoginPrompt && setShowLoginPrompt(true);
                      setIsMobileMenuOpen(false);
                    }}
                    className="block text-gray-600 hover:text-orange-600 transition-colors font-medium text-center w-full"
                  >
                    로그인
                  </button>
                )}

                {/* 관리자 대시보드 - 모바일 */}
                {isAdmin && (
                  <button 
                    onClick={() => {
                      setShowAdminDashboard && setShowAdminDashboard(true);
                      setIsMobileMenuOpen(false);
                    }}
                    className="flex items-center justify-center space-x-2 text-red-600 hover:text-red-700 transition-colors font-medium mt-2 w-full"
                  >
                    <div className="w-4 h-4 bg-red-500 text-white rounded text-xs font-bold flex items-center justify-center">
                      A
                    </div>
                    <span>관리자 대시보드</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Header;